import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";
import AudioPlayer from "./AudioPlayer";
import { stopAllPlayback } from "./audioPlayback";
import { cardCopy, samplesCountLabel } from "./cardCopy";

const sampleTitle = (sample, lng) => {
  if (sample.title) return sample.title;
  if (sample.performance_type && sample.performance_type.label) {
    return sample.performance_type.label[lng];
  }
  return "";
};

const sampleSubtitle = (sample, lng) => {
  if (!sample.title) return "";
  if (sample.performance_type && sample.performance_type.label) {
    return sample.performance_type.label[lng];
  }
  return "";
};

const SamplesModal = ({ open, onClose, talent, lng }) => {
  const copy = cardCopy(lng);

  useEffect(() => {
    if (!open) return undefined;

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
      stopAllPlayback();
    };
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  const samples = (talent.samples || []).filter((sample) => sample.audio_url);
  const code = talent.public_code || "";

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="samples-modal-title"
    >
      <button
        type="button"
        aria-label={copy.close}
        className="absolute inset-0 bg-purple/50 backdrop-blur-[2px]"
        onClick={onClose}
      />

      <div className="relative z-[1] w-full sm:max-w-[560px] max-h-[min(88dvh,88vh)] min-h-0 bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden mx-0 sm:mx-[16px]">
        <div className="shrink-0 flex items-start justify-between gap-[12px] px-[22px] pt-[20px] pb-[14px] border-b border-silver">
          <div>
            <p className="text-gray text-xs mb-[2px]">{code}</p>
            <h2
              id="samples-modal-title"
              className="text-purple text-xl font-semibold"
            >
              {copy.voiceSamples}
            </h2>
            <p className="text-gray text-sm mt-[2px]">
              {samplesCountLabel(samples.length, lng)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={copy.close}
            className="shrink-0 w-[40px] h-[40px] rounded-full bg-silver text-purple flex items-center justify-center hover:bg-purple hover:text-white transition-colors"
          >
            <IoClose className="w-[22px] h-[22px]" />
          </button>
        </div>

        <div
          data-testid="samples-list"
          className="samples-list min-h-0 flex-1 overflow-y-auto overscroll-contain px-[22px] py-[16px] flex flex-col gap-[10px]"
        >
          {samples.length === 0 ? (
            <p className="text-gray text-center py-[24px]">
              {copy.noSamples}
            </p>
          ) : (
            samples.map((sample) => (
              <AudioPlayer
                key={sample.id}
                src={sample.audio_url}
                title={sampleTitle(sample, lng)}
                subtitle={sampleSubtitle(sample, lng)}
              />
            ))
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SamplesModal;
